import React, { FC, useEffect, useMemo, useState } from 'react'

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable'
import { alpha, useTheme } from '@mui/material'
import Box from '@mui/material/Box'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useThemeMode } from '../../../theme/ThemeModeContext'
import { changeSmallWidgetsList } from '../DashboardSlice'
import { DrugStrip } from '../DrugStrip'
import { useGetSmallWidgets } from '../hooks'
import { SmallWidgetsName, SmallWidgetItem } from '../types'

import { SortableWidget } from './SortableWidget'

const filterBrokenWidgets = (item: string) => {
  // TODO: Remove avgTradeSize filter after fix avgTradeSize
  // TODO: Remove roi filter after fix roi
  return (
    [SmallWidgetsName.avgTradeSize, SmallWidgetsName.roi].includes(item as SmallWidgetsName) ===
    false
  )
}

export const SmallWidgetsSection: FC = () => {
  const { spacing, palette } = useTheme()
  const { resolvedMode } = useThemeMode()

  const dragHoverColor =
    resolvedMode === 'dark' ? alpha(palette.background.paper, 0.3) : palette.blue[400]

  const dispatch = useAppDispatch()

  const widgetsMap = useGetSmallWidgets()
  const { customize, smallWidgetsList } = useAppSelector(({ dashboard }) => dashboard)

  const initWidgetsOrder = Object.keys(smallWidgetsList)
    .filter((item) => smallWidgetsList[item as SmallWidgetsName])
    .filter(filterBrokenWidgets)
    .map((widgetName) => ({
      id: widgetName,
    }))

  const [widgets, setWidgets] = useState<SmallWidgetItem[]>(initWidgetsOrder as SmallWidgetItem[])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
  )

  const widgetIds = useMemo(() => widgets.map((widget) => widget.id), [widgets])

  const handleOrderChange = (updatedWidgets: SmallWidgetItem[]) => {
    setWidgets(updatedWidgets)
    const widgetsNames = updatedWidgets.reduce((acc, smallWidget) => {
      acc[smallWidget.id] = smallWidgetsList[smallWidget.id]
      return acc
    }, {} as Record<keyof typeof SmallWidgetsName, boolean>)
    Object.keys(SmallWidgetsName).forEach((item) => {
      if (!(item in widgetsNames)) {
        widgetsNames[item as SmallWidgetsName] = smallWidgetsList[item as SmallWidgetsName]
      }
    })

    dispatch(changeSmallWidgetsList(widgetsNames))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    if (!customize) {
      return
    }

    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = widgets.findIndex((widget) => widget.id === active.id)
    const newIndex = widgets.findIndex((widget) => widget.id === over.id)

    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    handleOrderChange(arrayMove(widgets, oldIndex, newIndex))
  }

  useEffect(() => {
    if (!customize) {
      const onlyVisible = Object.keys(smallWidgetsList)
        .filter((item) => smallWidgetsList[item as SmallWidgetsName])
        .filter(filterBrokenWidgets)
        .map((widgetName) => ({
          id: widgetName,
        }))
      setWidgets(onlyVisible as SmallWidgetItem[])
    } else {
      const fullList = Object.keys(smallWidgetsList)
        .filter(filterBrokenWidgets)
        .map((widgetName) => ({
          id: widgetName,
        }))
      setWidgets(fullList as SmallWidgetItem[])
    }
  }, [smallWidgetsList, customize])

  return (
    <Box
      sx={{
        '&:hover': {
          backgroundColor: customize ? dragHoverColor : 'unset',
          transition: '0.4s all',
        },
        padding: customize ? spacing(1) : 'unset',
        borderRadius: spacing(1),
      }}
    >
      {customize && <DrugStrip />}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToParentElement]}
      >
        <SortableContext items={widgetIds} strategy={rectSortingStrategy}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fit, minmax(${spacing(25.5)}, 1fr))`,
              gridAutoRows: '1fr',
              gap: spacing(2),
            }}
          >
            {widgets.map((widget) => (
              <SortableWidget key={widget.id} id={widget.id} disabled={!customize}>
                {widgetsMap[widget.id as keyof typeof widgetsMap]}
              </SortableWidget>
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  )
}
